import type { Agent } from './types'
import { area, currentlyCapped, eliteRecent, mlsPrimary } from './predicates'
import { daysUntil, monthDay, NOT_KNOWN_YET } from './format'
import { looksDoubled } from './address'

/**
 * Every way the roster can be narrowed, as data rather than as code.
 *
 * The point of this file is that the next filter is one entry in the list
 * below, not a new piece of state in `roster.tsx`, a new dropdown in `App.tsx`
 * and a new clause in the matcher. Three places to keep in step is how a
 * filter ends up applying to the count but not the list.
 *
 * It is also the vocabulary Pearl will use. When she is given the ability to
 * put a selection on screen, she will name a `key` from this list and a value
 * from `optionsFor(key)` — she does not get to invent a query. Whatever she
 * chooses, Renee sees the same list she would have got by using the dropdowns
 * herself, which is what makes a wrong guess visible rather than silent.
 *
 * One shape covers all of them: a filter asks each agent "which of your values
 * match this?" and keeps the agent if the chosen one is among them. Tags return
 * several values, most fields return one, and "is this blank" returns a word
 * either way. There is deliberately no second kind.
 */

export type FilterContext = {
  tagsByAgent: Map<string, string[]>
  /** Renee's own notes, so the search box can reach them. */
  notesByAgent?: Map<string, string>
  /**
   * The mailing list — who she has ticked. Only consulted when a scope asks for
   * `selectedOnly`, so every existing caller is unaffected by its absence.
   */
  selection?: ReadonlySet<string>
}

export type FilterDef = {
  /** Stable id. Used in the roster's state and, later, by Pearl. */
  key: string
  /** What the dropdown is called. */
  label: string
  /** The "no filter" option, e.g. "All areas". */
  all: string
  /** The three familiar ones stay on the toolbar; the rest live in the panel. */
  placement: 'toolbar' | 'panel'
  /** The values this agent matches. Empty means the agent matches nothing. */
  values: (a: Agent, ctx: FilterContext) => string[]
  /**
   * The complete value list, for filters whose options are fixed in code
   * rather than read off the data.
   *
   * Pearl cannot see the roster when she picks a value, so for these she can be
   * given the exact strings instead of guessing at them — which she did, asking
   * for "already expired" and getting an empty list. Filters whose values come
   * from the data (teams, regions, dates) have no `known` list and she is told
   * to check with a search first.
   */
  known?: string[]
  /**
   * True where one agent legitimately matches several values at once — tags,
   * and the nested licence windows. Everything else partitions the roster, and
   * the tests check that it does; this is how a filter opts out of that check
   * rather than quietly failing it.
   */
  multi?: true
  /** How a value is written on screen. Defaults to the value itself. */
  display?: (v: string) => string
  /** Option order. Defaults to alphabetical. */
  compare?: (a: string, b: string) => number
}

export const ON_FILE = 'On file'
export const MISSING = 'Missing'

/**
 * Mirrors Metabase's own "Non producing agents" card: zero pending
 * transactions, zero closed transactions, and zero active listings, all
 * at once. The three source columns are `integer not null default 0`
 * (sync-metabase.ts), so `?? 0` only guards a record this app has never
 * synced these fields for yet, not a real gap in Metabase's data.
 */
export const NON_PRODUCING = 'Non-producing'
export const PRODUCING = 'Producing'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const monthName = (m: number | null) => (m == null ? NOT_KNOWN_YET : MONTHS[m - 1] ?? NOT_KNOWN_YET)

/**
 * One formatter, built once and reused.
 *
 * `toLocaleDateString` with an options object costs about 90 microseconds a
 * call and is not cached between calls. This runs once per visible row per
 * render, so 785 rows made it about 130ms of blocking work on every keystroke.
 *
 * UTC is not optional. `license_expires` arrives as a bare `2027-06-30`, which
 * parses as UTC midnight; read back in New York it is the evening of the 29th,
 * and the dropdown would offer a date one day earlier than the detail panel
 * shows for the same agent.
 */
const DMY = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})

const asDMY = (value: string): string | null => {
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : DMY.format(d)
}

/** Chronological, with anything that is not a date last. */
const byDate = (a: string, b: string) => {
  const da = Date.parse(a)
  const db = Date.parse(b)
  if (Number.isNaN(da) && Number.isNaN(db)) return a.localeCompare(b)
  if (Number.isNaN(da)) return 1
  if (Number.isNaN(db)) return -1
  return da - db
}

/** Calendar order, with "Not known yet" last. */
const byMonth = (a: string, b: string) => {
  const ia = MONTHS.indexOf(a)
  const ib = MONTHS.indexOf(b)
  return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib)
}

/**
 * "August 2026" from a bare `date_became_capped` / `date_became_elite`, or
 * "Not known yet". UTC, for the reason `asDMY` above is: these arrive as a
 * bare `2026-08-15`, and reading the local calendar month back out in New
 * York can land a day early.
 *
 * Month and year together — not the bare month `birthday_month` and
 * `anniversary_month` bucket by. A birthday genuinely recurs with no year
 * attached, so collapsing years is correct for it; a cap or Elite date is one
 * forward-moving date per agent, and bucketing it by bare month name would
 * quietly merge every agent who ever capped in any August together,
 * regardless of year.
 */
const monthYear = (value: string | null): string => {
  if (!value) return NOT_KNOWN_YET
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return NOT_KNOWN_YET
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/**
 * Chronological by year then calendar month, "Not known yet" last — the same
 * idea as `byDate`, applied to values already collapsed to a month. Modelled
 * on `license_year`'s plain `localeCompare` ordering, not on `byMonth`, which
 * would sort "August 2025" and "August 2026" as the same bucket.
 */
const byMonthYear = (a: string, b: string) => {
  if (a === NOT_KNOWN_YET) return b === NOT_KNOWN_YET ? 0 : 1
  if (b === NOT_KNOWN_YET) return -1
  const [ma, ya] = a.split(' ')
  const [mb, yb] = b.split(' ')
  return Number(ya) - Number(yb) || MONTHS.indexOf(ma) - MONTHS.indexOf(mb)
}

/**
 * `joined_in` / `sc_licensed_in` deliberately do not offer full history.
 * This is a "who's new" search, not a permanent record of when everyone
 * joined, so its option list is bounded to a recent rolling window instead
 * of surfacing every month a record happens to hold.
 *
 * Floored at July 2026 rather than literally "6 months before today,"
 * because that is when this became a trustworthy field — data from before
 * that is exactly the kind of gap that had to be found and backfilled by
 * hand (see the 2026-09-05 build notes). Until January 2027 the window is
 * therefore July 2026 through the current month — fewer than six options,
 * and that is fine; it does not pad out fictitious earlier months just to
 * reach six. From January 2027 on it behaves as a true rolling window: the
 * oldest month drops off the moment a new one completes.
 *
 * `now` is only ever overridden by a test — every real caller uses the
 * default, same pattern as `daysToExpiry` / `startOfLocalDay` below.
 */
const JOINED_WINDOW_FLOOR = { year: 2026, month: 7 } // July 2026
const JOINED_WINDOW_MONTHS = 6

const monthIndex = (year: number, month: number) => year * 12 + (month - 1)

export function monthYearWindowed(value: string | null, now = new Date()): string {
  if (!value) return NOT_KNOWN_YET
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return NOT_KNOWN_YET
  const curIdx = monthIndex(now.getUTCFullYear(), now.getUTCMonth() + 1)
  const floorIdx = monthIndex(JOINED_WINDOW_FLOOR.year, JOINED_WINDOW_FLOOR.month)
  const startIdx = Math.max(floorIdx, curIdx - (JOINED_WINDOW_MONTHS - 1))
  const idx = monthIndex(d.getUTCFullYear(), d.getUTCMonth() + 1)
  if (idx < startIdx || idx > curIdx) return NOT_KNOWN_YET
  return monthYear(value)
}

/** On file before Missing, always — not alphabetical, which would flip them. */
const byPresence = (a: string) => (a === ON_FILE ? -1 : 1)

const presence = (get: (a: Agent) => string | null) => (a: Agent) =>
  [get(a) && String(get(a)).trim() ? ON_FILE : MISSING]

export const NO_EXPIRY = 'No date on file'

export const EXPIRED = 'Already expired'
export const WINDOWS = ['Within 30 days', 'Within 60 days', 'Within 90 days'] as const
const LATER = 'Later than 90 days'
const WINDOW_ORDER = [EXPIRED, ...WINDOWS, LATER, NO_EXPIRY]

/**
 * Days from today to a licence expiry, or null.
 *
 * `license_expires` is a bare calendar date and parses as UTC midnight, so
 * "today" has to be Renee's calendar day expressed the same way. Taking the
 * UTC date instead looks harmless and is wrong every evening: after 8pm in New
 * York the UTC date is already tomorrow, so a licence expiring TODAY came back
 * as "Already expired" while the detail panel — which formats in UTC — showed
 * today's date beside it.
 *
 * `todayLocal` is passed in rather than read here so the whole roster is judged
 * against one instant, and so a test can pin the clock.
 */
export function daysToExpiry(value: string | null, todayLocal = startOfLocalDay()): number | null {
  if (!value) return null
  const at = Date.parse(value)
  if (Number.isNaN(at)) return null
  return Math.round((at - todayLocal) / 86_400_000)
}

/** Renee's today, as the UTC midnight that a bare date would parse to. */
export function startOfLocalDay(now = new Date()): number {
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
}

export const FILTERS: FilterDef[] = [
  {
    key: 'area',
    label: 'Area',
    all: 'All areas',
    placement: 'toolbar',
    values: (a) => [area(a)],
  },
  {
    key: 'mls',
    label: 'MLS',
    all: 'All MLS',
    placement: 'toolbar',
    values: (a) => [mlsPrimary(a)],
  },
  {
    key: 'tag',
    label: 'Tag',
    all: 'All tags',
    placement: 'toolbar',
    values: (a, ctx) => ctx.tagsByAgent.get(a.rezen_id) ?? [],
    multi: true,
  },
  {
    key: 'license_expires',
    label: 'Licence expires',
    all: 'Any expiry date',
    placement: 'panel',
    // `||` rather than `??`: an empty string is as absent as a null, and an
    // option whose label is blank looks like a second "Any expiry date".
    values: (a) => [a.license_expires || NO_EXPIRY],
    display: (v) => asDMY(v) ?? v,
    compare: byDate,
  },
  {
    key: 'license_year',
    label: 'Licence expires in',
    all: 'Any year',
    placement: 'panel',
    // The year beside the exact date, not instead of it. Nearly every licence
    // in this roster falls on 30 June, so the two usually agree — but "2027"
    // is how Renee talks about a renewal cohort, and answering it with only
    // the 30 June group would quietly drop the rest.
    values: (a) => [a.license_expires ? a.license_expires.slice(0, 4) : NO_EXPIRY],
    compare: (x, y) => (x === NO_EXPIRY ? 1 : y === NO_EXPIRY ? -1 : x.localeCompare(y)),
  },
  {
    key: 'license_window',
    label: 'Licence status',
    all: 'Any licence status',
    placement: 'panel',
    /**
     * Several values at once, on purpose. A licence 20 days out is inside 30,
     * 60 and 90 days, so picking "Within 90 days" has to include it — the
     * alternative is three buckets that each hide the urgent ones.
     */
    values: (a) => {
      const days = daysToExpiry(a.license_expires)
      if (days == null) return [NO_EXPIRY]
      if (days < 0) return [EXPIRED]
      const out: string[] = []
      if (days <= 30) out.push(WINDOWS[0])
      if (days <= 60) out.push(WINDOWS[1])
      if (days <= 90) out.push(WINDOWS[2])
      if (out.length === 0) out.push(LATER)
      return out
    },
    compare: (x, y) => WINDOW_ORDER.indexOf(x) - WINDOW_ORDER.indexOf(y),
    known: [...WINDOW_ORDER],
    multi: true,
  },
  {
    key: 'address_check',
    label: 'Address looks odd',
    all: 'Any address',
    placement: 'panel',
    values: (a) => {
      // Trim BEFORE falling back, exactly as the export does. `x || y` on a
      // whitespace-only mailing address never reaches the home one, which
      // would file an agent under "No address" here and still print them a
      // label there.
      const raw =
        (a.mailing_address && a.mailing_address.trim()) ||
        (a.home_address && a.home_address.trim()) ||
        ''
      if (!raw) return ['No address']
      return [looksDoubled(raw) ? 'Worth checking' : 'Looks fine']
    },
    compare: (x, y) => (x === 'Worth checking' ? -1 : y === 'Worth checking' ? 1 : x.localeCompare(y)),
    known: ['Worth checking', 'Looks fine', 'No address'],
  },
  {
    key: 'region',
    label: 'Region',
    all: 'All regions',
    placement: 'panel',
    values: (a) => [a.region || 'Unassigned'],
  },
  {
    key: 'primary_market',
    label: 'Primary market',
    all: 'All markets',
    placement: 'panel',
    values: (a) => [a.primary_market || 'Unassigned'],
  },
  {
    key: 'team',
    label: 'Team',
    all: 'All teams',
    placement: 'panel',
    values: (a) => [a.team_name || 'Not on a team'],
  },
  {
    key: 'team_role',
    label: 'Team role',
    all: 'Any role',
    placement: 'panel',
    // Not "Not on a team" — an agent can be on a team with no role recorded,
    // and filing them under the same words as the Team filter's empty value
    // would mix two different facts into one option.
    values: (a) => [a.team_role || 'No role recorded'],
  },
  {
    key: 'birthday_month',
    label: 'Birthday month',
    all: 'Any birthday month',
    placement: 'panel',
    values: (a) => [monthName(a.birthday_month)],
    compare: byMonth,
    known: [...MONTHS, NOT_KNOWN_YET],
  },
  {
    key: 'anniversary_month',
    label: 'Anniversary month',
    all: 'Any anniversary month',
    placement: 'panel',
    values: (a) => [monthName(a.anniversary_month)],
    compare: byMonth,
    known: [...MONTHS, NOT_KNOWN_YET],
  },
  {
    key: 'capped_in',
    label: 'Capped in',
    all: 'Any month',
    placement: 'panel',
    // Same shape as `license_year`, not `birthday_month`: a data-driven set
    // of month+year cohorts, not a fixed twelve-month list. `date_became_capped`
    // is always the most recent cap date on file, so this answers "who capped
    // that month" for anyone who hasn't re-capped since — exactly the recent
    // lookup a congratulatory post needs, not a full history.
    values: (a) => [monthYear(a.date_became_capped)],
    compare: byMonthYear,
  },
  {
    key: 'elite_in',
    label: 'Reached Elite in',
    all: 'Any month',
    placement: 'panel',
    values: (a) => [monthYear(a.date_became_elite)],
    compare: byMonthYear,
  },
  {
    key: 'joined_in',
    label: 'Joined in',
    all: 'Any month',
    placement: 'panel',
    // `anniversary` holds the genuine join date for an ordinary joiner. It is
    // deliberately left null on an sc_licensed stub (that agent was already
    // with Real; nothing "joined" on the date the stub was created), so this
    // filter answers "who is new to Real" without also catching the SC cohort.
    values: (a) => [monthYearWindowed(a.anniversary)],
    compare: byMonthYear,
  },
  {
    key: 'sc_licensed_in',
    label: 'Newly SC-licensed in',
    all: 'Any month',
    placement: 'panel',
    // Mirrors `joined_in`, but for the other cohort: agents who were already
    // with Real and became newly active in SC. Those stubs have no
    // `anniversary` date, so this reads `first_seen_at` instead (set at
    // creation time) and gates on the `sc-licensed` tag so it only ever
    // matches that cohort, never an ordinary joiner. Everyone outside the
    // cohort falls into `NOT_KNOWN_YET` — same shape as "no cap date on
    // file" above, not an empty list: every non-multi filter here gives
    // every agent a value, or that agent silently vanishes from the option
    // list the moment it is applied.
    values: (a, ctx) =>
      (ctx.tagsByAgent.get(a.rezen_id) ?? []).includes('sc-licensed')
        ? [monthYearWindowed(a.first_seen_at)]
        : [NOT_KNOWN_YET],
    compare: byMonthYear,
  },
  {
    key: 'mailing_address',
    label: 'Mailing address',
    all: 'Any mailing address',
    placement: 'panel',
    values: presence((a) => a.mailing_address),
    compare: (a, b) => byPresence(a) - byPresence(b),
    known: [ON_FILE, MISSING],
  },
  {
    key: 'home_address',
    label: 'Home address',
    all: 'Any home address',
    placement: 'panel',
    values: presence((a) => a.home_address),
    compare: (a, b) => byPresence(a) - byPresence(b),
    known: [ON_FILE, MISSING],
  },
  {
    key: 'photo',
    label: 'Photo',
    all: 'Any photo',
    placement: 'panel',
    values: presence((a) => a.headshot_path),
    compare: (a, b) => byPresence(a) - byPresence(b),
    known: [ON_FILE, MISSING],
  },
  {
    key: 'producing',
    label: 'Producing status',
    all: 'Any producing status',
    placement: 'panel',
    values: (a) =>
      [(a.pending_transactions ?? 0) === 0 &&
       (a.closed_transactions ?? 0) === 0 &&
       (a.active_listings ?? 0) === 0
        ? NON_PRODUCING
        : PRODUCING],
    known: [NON_PRODUCING, PRODUCING],
  },
]

export const FILTER_BY_KEY = new Map(FILTERS.map((f) => [f.key, f]))

/** The value meaning "this filter is not narrowing anything". */
export const ANY = ''

export type FilterState = Record<string, string>

/**
 * Does this agent survive every active filter?
 *
 * This lives here, not in the provider, so that the same function answers for
 * the screen, for the export, and for anything Pearl is later allowed to set.
 * Three implementations of "which agents" is how an export ends up disagreeing
 * with the list it was taken from.
 */
export function matchesFilters(a: Agent, filters: FilterState, ctx: FilterContext): boolean {
  for (const def of FILTERS) {
    const chosen = filters[def.key]
    if (chosen === undefined || chosen === ANY) continue
    if (!def.values(a, ctx).includes(chosen)) return false
  }
  return true
}

/**
 * The filters that are actually narrowing something.
 *
 * Counted from the definitions rather than from the state's keys, so a key
 * nobody defined — the mistake Pearl will eventually make when she names a
 * filter — cannot show up as "More filters · 1" while nothing is filtered.
 */
export function activeFilterKeys(filters: FilterState): string[] {
  return FILTERS.filter((f) => {
    const v = filters[f.key]
    return v !== undefined && v !== ANY
  }).map((f) => f.key)
}

const digitsOnly = (v: string) => v.replace(/\D+/g, '')

/**
 * Free text covers everything a person might type: the name, where they are,
 * how to reach them, and — since photos and filters went in — their phone,
 * addresses and licence number, which used to be invisible to search.
 *
 * A phone number is matched on its digits alone. Renee types 8035541202, the
 * roster holds "(+1) 803-554-1202", and a plain substring test finds nothing.
 *
 * ── A WHOLE NAME, WHEN THE NAME IS TWO COLUMNS ────────────────────────────
 *
 * First and last name are separate columns and every field was tested
 * independently, so "Chris Bice" was checked against "Chris" and against
 * "Bice" and matched neither: typing somebody's actual name returned **no
 * agents**, while typing half of it found them. Found by accident on
 * 2026-08-18 while looking for a test record.
 *
 * So after the whole phrase fails, the words are tried separately. Three rules
 * keep that from turning the search into a net, and each is here because the
 * version without it did something bad:
 *
 *   1. **The phrase is tried first, against everything.** Anything that
 *      searched successfully before still searches exactly the same way, and
 *      notes and addresses stay findable by the phrases people remember. Only
 *      a *failed* search gets taken apart.
 *
 *   2. **Words are matched against who somebody IS, not where they live.**
 *      Names, team, role, region, market, service area, email, tags. Not
 *      addresses, not notes, not the licence or the phone. With addresses in,
 *      "dana reed" found Dana Whitfield of 104 Reedy Creek Road — and nothing
 *      on the row says which field matched, so she is just another Dana in a
 *      list that feeds Export, Tag and the Email count.
 *
 *   3. **Every word must be at least three characters.** Without that, "a b"
 *      matched all 786 people — two one-letter substrings across fourteen
 *      fields — and offered "Email 786" with a plausible number as the only
 *      warning.
 *
 * It is still looser than a phrase search in one respect: "Talia Reid" finds a
 * Reid on Talia's team as well as an agent called Talia Reid. That is a name
 * that plausibly meant either, which is not the same kind of wrong as finding
 * nobody at all.
 */
export function matchesSearch(a: Agent, search: string, ctx: FilterContext): boolean {
  const q = search.trim().toLowerCase()
  if (!q) return true

  const text = [
    a.first_name,
    a.last_name,
    a.team_name,
    a.team_role,
    a.primary_market,
    a.region,
    a.email,
    a.join_real_email,
    a.license_number,
    a.home_address,
    a.mailing_address,
    a.office_address,
    a.service_area,
    // The raw MLS, not mlsPrimary() — its "Unassigned" fallback would make
    // typing "unassigned" return every agent who has no MLS, from a word that
    // appears nowhere in their record.
    (a.mls as { primary?: unknown } | null)?.primary,
    ...(ctx.tagsByAgent.get(a.rezen_id) ?? []),
    // Renee's own note. Reading one is not writing one — the rule that only a
    // human puts words in a note is untouched. Finding "she mentioned moving
    // to Charlotte" six months later is the whole reason notes get written.
    ctx.notesByAgent?.get(a.rezen_id),
  ]
  if (text.filter(Boolean).some((v) => String(v).toLowerCase().includes(q))) return true

  const qDigits = digitsOnly(q)
  if (qDigits.length >= 3) {
    for (const v of [a.phone, a.license_number]) {
      if (v && digitsOnly(v).includes(qDigits)) return true
    }
  }

  /**
   * Everything above this line is exactly what it always was, and runs first —
   * so a single-word search costs precisely what it used to, and this tail only
   * runs for an agent the search had already rejected.
   *
   * Split on anything that is not a letter or a number rather than on spaces
   * alone, so "Bice, Chris" works. That is the order the roster itself sorts
   * and displays in, and it failed the same silent way as the bug this fixes.
   */
  const words = q.split(/[^\p{L}\p{N}]+/u).filter(Boolean)
  if (words.length < 2 || words.some((w) => w.length < 3)) return false

  const who = [
    a.first_name,
    a.last_name,
    a.team_name,
    a.team_role,
    a.region,
    a.primary_market,
    a.service_area,
    a.email,
    a.join_real_email,
    // The raw MLS again, not `mlsPrimary()` — its "Unassigned" fallback would
    // make "unassigned" a word that matches every agent who has no MLS.
    (a.mls as { primary?: unknown } | null)?.primary,
    ...(ctx.tagsByAgent.get(a.rezen_id) ?? []),
  ]
  const hay = who.filter(Boolean).map((v) => String(v).toLowerCase())
  return words.every((w) => hay.some((v) => v.includes(w)))
}

/**
 * The agent lists, as a value rather than only a type.
 *
 * The composer is reached from a tab and has to be told which one — a URL
 * carries that across, and a URL can say anything. `isTabKey` is what turns an
 * arbitrary string back into a tab, and it has to be checkable at runtime. The
 * type is derived from the array so the two cannot drift.
 */
export const TAB_KEYS = ['all', 'capped', 'elite', 'leaders'] as const

export type TabKey = (typeof TAB_KEYS)[number]

export const isTabKey = (v: unknown): v is TabKey =>
  typeof v === 'string' && (TAB_KEYS as readonly string[]).includes(v)

/**
 * What each tab is called on screen. One copy — `Email.tsx`'s composer names
 * the list it took from this, and `Scheduled.tsx` names a recurring
 * campaign's target group from the same source (2026-08-22), rather than
 * each screen keeping its own copy that could quietly drift from `AGENT_TABS`
 * in `App.tsx`.
 */
export const TAB_LABEL: Record<TabKey, string> = {
  all: 'All',
  capped: 'Capped',
  elite: 'Elite (12 mo)',
  leaders: 'Team Leaders',
}

export function inTab(a: Agent, tab: TabKey): boolean {
  if (tab === 'capped') return currentlyCapped(a)
  if (tab === 'elite') return eliteRecent(a)
  if (tab === 'leaders') return a.is_team_leader
  return true
}

export type Scope = {
  tab: TabKey
  filters: FilterState
  search: string
  includeDeparted: boolean
  /**
   * Show the mailing list, and nothing else.
   *
   * **This replaces the question rather than narrowing it.** The tab, the
   * dropdowns, the search box and the departed toggle are all ignored while it
   * is on, and that is the entire point: a mailing list is built by ticking
   * *across* filters — Greenville, then a team that spans four regions — so the
   * filter that produced the last chunk is still set at the moment she asks to
   * see the list. ANDing it in showed 13 of 273, and Export and Tag, which act
   * on whatever is on screen, would have acted on the 13.
   *
   * Departed agents on the list are shown too. One can only get there by being
   * ticked deliberately, and a list that cannot display everyone on it leaves a
   * count nobody can reconcile.
   *
   * Absent, it is off, so saved views, What's Missing and Pearl are unaffected
   * without knowing it exists.
   */
  selectedOnly?: boolean
}

/**
 * Which agents. The whole question, in one place.
 *
 * The tab, the departed toggle, the dropdowns and the search box are four
 * separate ideas that have to combine the same way everywhere they are asked:
 * on screen, in the tab counts, and — next — in whatever gets exported. When
 * "which agents" is written out more than once, the export eventually disagrees
 * with the list it was taken from, and nobody notices until Renee posts 347
 * letters.
 */
export function selectAgents(all: Agent[], scope: Scope, ctx: FilterContext): Agent[] {
  /**
   * The mailing list answers on its own. Everything else is skipped, including
   * the departed toggle — see `Scope.selectedOnly`. An empty selection with the
   * scope on returns nobody rather than everybody: failing open here would put
   * the whole roster behind a button labelled with a much smaller number.
   */
  if (scope.selectedOnly) {
    const picked = ctx.selection
    if (!picked || picked.size === 0) return []
    return all.filter((a) => picked.has(a.rezen_id))
  }

  return all.filter((a) => {
    // Departed agents are still rows — a soft state, never a delete. Hidden by
    // default, but the toggle keeps them reachable and their history intact.
    if (!scope.includeDeparted && !a.is_active) return false
    if (!inTab(a, scope.tab)) return false
    return matchesFilters(a, scope.filters, ctx) && matchesSearch(a, scope.search, ctx)
  })
}

/**
 * The options a dropdown offers, taken from the roster itself — so a filter can
 * never offer a choice that matches nobody, and a value that exists in the data
 * can never be missing from the list.
 */
export function optionsFor(
  key: string,
  agents: Agent[],
  ctx: FilterContext,
): { value: string; label: string }[] {
  const def = FILTER_BY_KEY.get(key)
  if (!def) return []
  const seen = new Set<string>()
  for (const a of agents) for (const v of def.values(a, ctx)) seen.add(v)
  const list = Array.from(seen)
  list.sort(def.compare ?? ((x, y) => x.localeCompare(y)))
  return list.map((v) => ({ value: v, label: def.display ? def.display(v) : v }))
}

/**
 * How the list is ordered. Same idea as the filters: one entry per ordering,
 * and nothing else in the app needs to know they exist.
 */
export type SortDef = {
  key: string
  label: string
  compare: (a: Agent, b: Agent) => number
}

const name = (a: Agent) =>
  `${a.last_name ?? ''} ${a.first_name ?? ''}`.trim().toLowerCase()

/** Missing values sort last in every ordering. A blank is not "soonest". */
const rank = (v: number | null) =>
  v == null || Number.isNaN(v) ? Number.POSITIVE_INFINITY : v

/**
 * Compare by a number that may be missing, falling back to the name.
 *
 * Written as a comparison rather than a subtraction on purpose: missing values
 * rank as Infinity, and `Infinity - Infinity` is NaN. A comparator that returns
 * NaN leaves the order up to the engine, and it would have done so for the
 * majority of the roster — only 199 of 785 birthdays are known — while looking
 * correct, because the rows arrive from the database in name order anyway.
 */
const byRank = (ka: number | null, kb: number | null, a: Agent, b: Agent) => {
  const x = rank(ka)
  const y = rank(kb)
  if (x !== y) return x < y ? -1 : 1
  return name(a).localeCompare(name(b))
}

const expiryMs = (a: Agent) => (a.license_expires ? Date.parse(a.license_expires) : null)

export const SORTS: SortDef[] = [
  {
    key: 'name',
    label: 'Last name (A–Z)',
    compare: (a, b) => name(a).localeCompare(name(b)),
  },
  {
    key: 'license_expires',
    label: 'Licence expiry (soonest first)',
    compare: (a, b) => byRank(expiryMs(a), expiryMs(b), a, b),
  },
  {
    key: 'birthday',
    label: 'Birthday (coming up first)',
    compare: (a, b) =>
      byRank(
        daysUntil(a.birthday_month, a.birthday_day),
        daysUntil(b.birthday_month, b.birthday_day),
        a,
        b,
      ),
  },
  {
    key: 'anniversary',
    label: 'Work anniversary (coming up first)',
    compare: (a, b) =>
      byRank(
        daysUntil(a.anniversary_month, a.anniversary_day),
        daysUntil(b.anniversary_month, b.anniversary_day),
        a,
        b,
      ),
  },
]

export const SORT_BY_KEY = new Map(SORTS.map((s) => [s.key, s]))
export const DEFAULT_SORT = 'name'

/**
 * What the list shows under each name. Following the sort means choosing
 * "birthday coming up" and seeing birthdays, rather than seeing the team name
 * and having to take the order on trust.
 */
export function sortDetail(a: Agent, sortKey: string): string | null {
  if (sortKey === 'license_expires') {
    if (!a.license_expires) return `Licence expiry ${NOT_KNOWN_YET.toLowerCase()}`
    const shown = asDMY(a.license_expires)
    return shown ? `Licence expires ${shown}` : null
  }
  if (sortKey === 'birthday') {
    const md = monthDay(a.birthday_month, a.birthday_day)
    return md ? `Birthday ${md}` : `Birthday ${NOT_KNOWN_YET.toLowerCase()}`
  }
  if (sortKey === 'anniversary') {
    const md = monthDay(a.anniversary_month, a.anniversary_day)
    return md ? `Anniversary ${md}` : `Anniversary ${NOT_KNOWN_YET.toLowerCase()}`
  }
  return null
}
